import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Shield, AlertTriangle, CheckCircle2, Image as ImageIcon, ExternalLink, Search } from "lucide-react";

const BUCKET_META = {
    custom_curated: { label: "Custom curated", priority: "ok", description: "Hand-selected high-quality archive imagery." },
    user_uploaded: { label: "User uploaded (curated)", priority: "ok", description: "Owner-uploaded authentic photos (Betz, Buga, etc.)." },
    wikimedia_or_public_domain: { label: "Wikimedia / Public domain", priority: "ok", description: "Legitimate public-domain sources." },
    ai_generated: { label: "AI generated", priority: "review", description: "Reconstructions from your AI sketch pipeline. Review for production fit." },
    stock_pexels: { label: "Stock photography (Pexels)", priority: "high", description: "Generic stock imagery that does not depict the case." },
    other: { label: "Other / Unverified", priority: "review", description: "Sources that don't fit a known bucket." },
    missing: { label: "Missing thumbnail", priority: "critical", description: "No image set." },
};

const PRIORITY_COLOR = {
    critical: "#ef4444",
    high: "#f59e0b",
    review: "#a78bfa",
    ok: "#22c55e",
};

export default function ThumbnailCuration() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");

    useEffect(() => {
        if (authLoading) return;
        let cancelled = false;
        Promise.resolve().then(async () => {
            if (!user) {
                if (!cancelled) { setErr("Sign in required"); setLoading(false); }
                return;
            }
            try {
                const r = await api.get("/admin/thumbnail-audit");
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.status === 403 ? "Admin or Research Council access required" : "Failed to load audit");
            }
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [authLoading, user]);

    if (loading) return <div className="ok-container py-12 font-mono text-xs opacity-50">Loading audit…</div>;
    if (err) return (
        <div className="ok-container py-12">
            <div className="ok-card p-8 text-center max-w-md mx-auto">
                <Shield className="w-10 h-10 mx-auto opacity-40 mb-3" />
                <h2 className="font-display text-xl mb-2">Restricted</h2>
                <p className="opacity-70 text-sm mb-4">{err}</p>
                <Link to="/login" className="ok-btn-primary inline-flex text-sm">Sign in</Link>
            </div>
        </div>
    );

    const visibleBuckets = Object.entries(data.buckets).filter(([k, v]) => {
        if (filter !== "all" && filter !== k) return false;
        return v.length > 0;
    });

    function matchesQuery(item) {
        if (!q) return true;
        const needle = q.toLowerCase();
        return (item.title || "").toLowerCase().includes(needle) || (item.image_label || "").toLowerCase().includes(needle);
    }

    return (
        <div className="ok-container py-10 max-w-7xl" data-testid="thumbnail-curation-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3 flex items-center gap-2">
                <Shield className="w-3 h-3" /> Admin · Thumbnail Curation
            </div>
            <h1 className="font-display text-3xl mb-2">Thumbnail Audit ({data.total} cases)</h1>
            <p className="opacity-70 text-sm mb-6 max-w-2xl">Read-only audit of every case thumbnail by source bucket. Auto-replacement is intentionally disabled. Work through the queue manually, upload replacements via the asset pipeline, then re-run.</p>

            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-8">
                {Object.entries(data.summary).map(([k, n]) => {
                    const meta = BUCKET_META[k] || { label: k, priority: "review" };
                    const color = PRIORITY_COLOR[meta.priority] || "#888";
                    return (
                        <button
                            key={k}
                            onClick={() => setFilter(filter === k ? "all" : k)}
                            className={`ok-card p-3 text-left transition ${filter === k ? "border-[var(--ok-accent)]" : ""}`}
                            data-testid={`bucket-filter-${k}`}
                        >
                            <div className="font-display text-2xl tabular-nums" style={{ color }}>{n}</div>
                            <div className="text-[10px] uppercase font-mono tracking-wide opacity-70 mt-1 leading-tight">{meta.label}</div>
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-3 mb-5">
                <div className="ok-input flex items-center gap-2 flex-1 max-w-md">
                    <Search className="w-4 h-4 opacity-50" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by case title…" className="bg-transparent flex-1 outline-none text-sm" data-testid="thumbnail-search" />
                </div>
                {filter !== "all" && (
                    <button onClick={() => setFilter("all")} className="text-xs font-mono opacity-60 hover:text-[var(--ok-accent)]">Clear filter ({filter}) ×</button>
                )}
            </div>

            {visibleBuckets.length === 0 ? (
                <div className="ok-card p-8 text-center">
                    <CheckCircle2 className="w-8 h-8 mx-auto text-[var(--ok-accent)] mb-2" />
                    <p className="opacity-70 text-sm">No items in this category.</p>
                </div>
            ) : (
                visibleBuckets.map(([k, items]) => {
                    const meta = BUCKET_META[k];
                    const filtered = items.filter(matchesQuery);
                    if (filtered.length === 0) return null;
                    return (
                        <section key={k} className="mb-10" data-testid={`bucket-section-${k}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h2 className="font-display text-xl flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full" style={{ background: PRIORITY_COLOR[meta.priority] }} />
                                        {meta.label}
                                        <span className="text-sm opacity-50 font-mono">({filtered.length})</span>
                                    </h2>
                                    <p className="text-xs opacity-60 mt-0.5">{meta.description}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {filtered.map((it) => (
                                    <div key={it.slug || it.title} className="ok-card overflow-hidden" data-testid={`thumb-row-${it.slug || ""}`}>
                                        <div className="aspect-video bg-[var(--ok-surface-hover)] flex items-center justify-center overflow-hidden">
                                            {it.current_url ? (
                                                <img src={it.current_url.startsWith("http") ? it.current_url : `${process.env.REACT_APP_BACKEND_URL}${it.current_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                                            ) : (
                                                <ImageIcon className="w-8 h-8 opacity-30" />
                                            )}
                                        </div>
                                        <div className="p-3">
                                            <div className="text-sm font-medium leading-tight line-clamp-2 mb-1">{it.title}</div>
                                            <div className="text-[10px] uppercase tracking-wide font-mono opacity-60 truncate">{it.image_label || "—"}</div>
                                            {it.image_source && (
                                                <div className="text-[10px] opacity-50 truncate mt-0.5">{it.image_source}</div>
                                            )}
                                            <div className="flex items-center gap-2 mt-2">
                                                {it.slug && (
                                                    <Link to={`/encounters/by-slug/${it.slug}`} className="text-[10px] font-mono opacity-70 hover:text-[var(--ok-accent)] flex items-center gap-1" data-testid={`thumb-view-${it.slug}`}>
                                                        View case <ExternalLink className="w-2.5 h-2.5" />
                                                    </Link>
                                                )}
                                                {it.needs_review && (
                                                    <span className="ok-tag text-[10px]" style={{ borderColor: "#f59e0b", color: "#f59e0b" }}>
                                                        <AlertTriangle className="w-2.5 h-2.5" /> NEEDS REVIEW
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })
            )}

            <div className="ok-card p-5 mt-8 max-w-3xl">
                <h3 className="font-display text-lg mb-2">How to clear this queue</h3>
                <ol className="text-sm opacity-80 space-y-1.5 leading-relaxed list-decimal pl-5">
                    <li>Filter by <strong>Stock photography</strong> or <strong>Missing</strong>. These are the highest priority.</li>
                    <li>For each case, source a public-domain or Wikimedia image (or own photography).</li>
                    <li>Upload to the asset pool by chat, or place directly into <code className="font-mono text-xs opacity-70">/app/backend/static/case_thumbs/</code>.</li>
                    <li>Update the case&apos;s <code className="font-mono text-xs opacity-70">sketch_url</code> in <code className="font-mono text-xs opacity-70">cases.json</code> and clear <code className="font-mono text-xs opacity-70">needs_image_review</code>.</li>
                    <li>Refresh this page. The bucket counters will drop in real time.</li>
                </ol>
            </div>
        </div>
    );
}
